/**
 * Press outlets — the ONE canonical "As Featured" list (owner list, 07/20/2026).
 *
 * Each outlet renders as its official full-color logo from the `press-logos`
 * Supabase storage bucket (env-derived URL — never a hardcoded project host).
 * Logos are display-only: NOT links (owner ruling 07/20/2026).
 *
 * Shared by the sitewide footer band, homepage, /about-us, /press, and the
 * llms.txt media list so the surfaces can never drift. Logo sourcing record:
 * docs/Operations.md.
 */
export interface PressOutlet {
  name: string
  /** Object key inside the public `press-logos` storage bucket. */
  logo: string
  /**
   * The logo file's own size in pixels (an SVG's width and height, or its
   * viewBox when it has none), measured from the stored file 10/14/2026. The
   * band renders them as the img's width and height so a lazy-loaded logo has
   * its shape before it arrives (FRIDGE-1505); CSS still sets the drawn size.
   * Replacing a logo file means measuring the new one.
   */
  width: number
  height: number
}

/**
 * The date of the last full verification pass over this list (FRIDGE-601).
 * Rendered wherever the outlet count renders, so the claim always says how
 * current it is — a dated count can never "seem old", it either is current or
 * states exactly when it was last proven.
 *
 * The refresh process (run it, then move this date) is documented in
 * docs/Operations.md § "Press citations — evidence bank & refresh process".
 * Format MM/DD/YYYY (the house body-text date format).
 */
export const PRESS_OUTLETS_VERIFIED_AS_OF = '08/28/2026'

export const PRESS_OUTLETS: readonly PressOutlet[] = [
  { name: 'New York Post', logo: 'new-york-post.svg', width: 370, height: 50 },
  { name: 'Yahoo', logo: 'yahoo.svg', width: 1000, height: 277 },
  { name: 'AOL', logo: 'aol.svg', width: 1000, height: 353 },
  { name: 'Food & Wine', logo: 'food-and-wine.svg', width: 241, height: 32 },
  { name: 'Mirror', logo: 'mirror.svg', width: 719, height: 272 },
  { name: 'Express', logo: 'express.png', width: 600, height: 126 },
  { name: 'WikiHow', logo: 'wikihow.svg', width: 463, height: 76 },
  { name: 'Chowhound', logo: 'chowhound.svg', width: 700, height: 84 },
  { name: 'Daily Dot', logo: 'daily-dot.svg', width: 1195, height: 235 },
  { name: 'First for Women', logo: 'first-for-women.png', width: 847, height: 277 },
  { name: 'Daily Record', logo: 'daily-record.svg', width: 141, height: 80 },
  { name: 'northjersey.com', logo: 'northjersey.svg', width: 371, height: 45 },
  { name: 'Rupa Health', logo: 'rupa-health.svg', width: 200, height: 36 },
  { name: 'Grokipedia', logo: 'grokipedia.png', width: 688, height: 156 },
  { name: 'The Fact Site', logo: 'the-fact-site.png', width: 580, height: 190 },
  { name: 'Daily Dish', logo: 'daily-dish.png', width: 741, height: 568 },
  { name: 'A Pinch of Healthy', logo: 'a-pinch-of-healthy.png', width: 600, height: 300 },
  { name: 'Cupcakes and Cutlery', logo: 'cupcakes-and-cutlery.png', width: 708, height: 234 },
  { name: 'Food Huggers', logo: 'food-huggers.png', width: 240, height: 142 },
  { name: 'UK.com', logo: 'uk-com.svg', width: 295, height: 55 },
  { name: 'Food&HotelAsia', logo: 'foodnhotelasia.png', width: 428, height: 134 },
  // Added 08/28/2026 (FRIDGE-601): live citing article verified at
  // thedailymeal.com/2219780/refrigerator-brand-avoid/ ("according to a
  // Fridge.com data analysis, 37.4% of U.S. households have at least two
  // working refrigerators"), and the site's largest press referrer in
  // PostHog since 07/20 (163 visits / 61 visitors). Evidence bank:
  // docs/Operations.md.
  { name: 'The Daily Meal', logo: 'the-daily-meal.svg', width: 1479, height: 295 },
] as const

/**
 * Public URL for an outlet's logo. Returns the site-relative
 * fridge.com/press-logos/* proxy path (next.config rewrite → Supabase Storage)
 * so the rendered <img> only ever shows a fridge.com host, never a
 * *.supabase.co storage host (owner directive 07/20). Same-origin, so it
 * resolves against localhost in dev and fridge.com in prod.
 */
export function pressLogoUrl(outlet: PressOutlet): string {
  return `/press-logos/${outlet.logo}`
}

/**
 * Back-compat alias: earlier chrome code imported VERIFIED_PRESS_OUTLETS
 * (name+url tiles). The band is now logo-only; the alias keeps the shared
 * single-list contract for any straggler import.
 */
export const VERIFIED_PRESS_OUTLETS = PRESS_OUTLETS
